// Tweens between numeric prop changes so headline values (weight, height,
// daily totals) animate to fresh data rather than swapping. Uses
// requestAnimationFrame with an ease-out curve. Set `duration={0}` to
// disable for callers that don't want motion.

import { useEffect, useRef, useState } from 'react';

type Props = {
  value: number;
  /** Output formatter — call site decides precision, units, separators. */
  format: (n: number) => string;
  /** Tween length in ms; default 400. */
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
};

const ease = (t: number) => 1 - (1 - t) * (1 - t); // easeOutQuad

export function AnimatedNumber({ value, format, duration = 400, className, style }: Props) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (value === display) return;
    if (duration <= 0) {
      setDisplay(value);
      return;
    }
    fromRef.current = display;
    const start = performance.now();
    const from = fromRef.current;
    const to = value;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      setDisplay(from + (to - from) * ease(t));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return (
    <span className={className} style={style}>
      {format(display)}
    </span>
  );
}
